/**
 * Client-side FlightSegment model with computed properties and methods
 */

import type { FlightSegment as FlightSegmentInput } from '@elara/domain/models/schemas/flight-schemas';

import type { MapPoint } from '@elara/client-core/ui-contracts/map-point';
import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

import { FlightSegmentModelSpec } from '@elara/domain/models/TransientModelSpecs';

import { createLocalModel } from '#firebase/createLocalModel.js';
import { formatDuration, formatTimeInOriginalTimezone } from '#lib/date-utils.js';
import { isValidImageUrl } from '#lib/utils.js';

export const FlightSegmentModelFactory = createLocalModel(FlightSegmentModelSpec, (data) => ({
  /**
   * Departure time as ISO datetime string (for date extraction, e.g. YYYY-MM-DD)
   */
  get departure(): string {
    return data.departure;
  },

  /**
   * Formatted departure time in original timezone
   */
  get formattedDepartureTime(): string {
    return formatTimeInOriginalTimezone(data.departure, 'h:mm a');
  },

  /**
   * Formatted departure date
   */
  get formattedDepartureDate(): string {
    return formatTimeInOriginalTimezone(data.departure, 'MMM d');
  },

  get longDepartureDate(): string {
    return formatTimeInOriginalTimezone(data.departure, 'EEEE MMMM d');
  },

  /**
   * Formatted departure date and time in original timezone
   */
  get formattedDepartureDateTime(): string {
    return formatTimeInOriginalTimezone(data.departure, 'h:mm a - EEEE MMM d');
  },

  /**
   * Formatted arrival time in original timezone
   */
  get formattedArrivalTime(): string {
    return formatTimeInOriginalTimezone(data.arrival, 'h:mm a');
  },

  /**
   * Formatted arrival date
   */
  get formattedArrivalDate(): string {
    return formatTimeInOriginalTimezone(data.arrival, 'MMM d');
  },

  get longArrivalDate(): string {
    return formatTimeInOriginalTimezone(data.arrival, 'EEEE MMMM d');
  },

  /**
   * Formatted arrival date and time in original timezone
   */
  get formattedArrivalDateTime(): string {
    return formatTimeInOriginalTimezone(data.arrival, 'h:mm a - EEEE MMM d');
  },

  /**
   * Formatted duration string
   */
  get formattedDuration(): string {
    if (!data.duration) return '';
    return formatDuration(data.duration);
  },

  /**
   * Origin airport code
   */
  get originCode(): string {
    return data.origin.iata_code;
  },

  /**
   * Destination airport code
   */
  get destinationCode(): string {
    return data.destination.iata_code;
  },

  /**
   * Route string (e.g., "JFK → LAX")
   */
  get route(): string {
    return `${this.originCode} → ${this.destinationCode}`;
  },

  /** Operating/marketing airline name, if known. */
  get airlineName(): string | undefined {
    return data.airline.name ?? undefined;
  },

  /**
   * Airline logo URL if valid, otherwise undefined
   */
  get airlineLogoUrl(): string | undefined {
    return isValidImageUrl(data.airline.logo_url) ? data.airline.logo_url : undefined;
  },

  /**
   * Airline fallback text (ICAO code or IATA code)
   */
  get airlineFallbackText(): string {
    return data.airline.icao_code?.slice(0, 2) || data.airline.iata_code?.slice(0, 2) || '';
  },

  /**
   * Check if flight is overnight
   */
  get isOvernight(): boolean {
    return data.overnight ?? false;
  },

  get mapPoints(): MapPoint[] {
    return [
      { id: data.origin.iata_code, address: data.origin.airport_name, label: data.origin.airport_name },
      { id: data.destination.iata_code, address: data.destination.airport_name, label: data.destination.airport_name },
    ];
  },

  /**
   * Get amenity icon name for a given amenity string
   */
  getAmenityIcon(amenity: string): IconName {
    const lowerAmenity = amenity.toLowerCase();

    if (lowerAmenity.includes('wi-fi') || lowerAmenity.includes('wifi')) {
      return 'wifi';
    }
    if (lowerAmenity.includes('power')) {
      return 'power';
    }
    if (lowerAmenity.includes('usb')) {
      return 'usb';
    }
    if (lowerAmenity.includes('video') || lowerAmenity.includes('on-demand')) {
      return 'live_tv';
    }
    if (lowerAmenity.includes('emissions')) {
      return 'globe';
    }
    if (lowerAmenity.includes('contrail')) {
      return 'plane_contrails';
    }
    if (lowerAmenity.includes('above average legroom')) {
      return 'airline_seat_legroom_extra';
    }
    if (lowerAmenity.includes('below average legroom')) {
      return 'airline_seat_legroom_reduced';
    }
    if (lowerAmenity.includes('average legroom')) {
      return 'airline_seat_legroom_normal';
    }
    if (lowerAmenity.includes('lie flat seat')) {
      return 'airline_seat_flat';
    }
    if (lowerAmenity.includes('individual suite')) {
      return 'airline_seat_individual_suite';
    }

    return 'check';
  },

  /**
   * Calculate layover time in minutes between this flight and the next flight
   */
  getLayoverTime(nextFlight: FlightSegmentInput): number {
    try {
      const arrivalTime = new Date(data.arrival);
      const departureTime = new Date(nextFlight.departure);

      if (Number.isNaN(arrivalTime.getTime()) || Number.isNaN(departureTime.getTime())) {
        return 0;
      }

      const layoverMinutes = (departureTime.getTime() - arrivalTime.getTime()) / (1000 * 60);
      return Math.max(layoverMinutes, 0);
    } catch {
      return 0;
    }
  },
}));

export type FlightSegmentModel = ReturnType<(typeof FlightSegmentModelFactory)['from']>;

/**
 * Creates a client-side FlightSegment instance with computed properties and methods
 */
export function createFlightSegmentModel(data: FlightSegmentInput): FlightSegmentModel {
  return FlightSegmentModelFactory.from(data);
}
